import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Star as StarIcon,
  MonetizationOn as MonetizationOnIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { adminService } from '../../services/api';

interface TfiatDistributionData {
  user_id: number;
  email: string;
  is_admin: boolean;
  full_name: string;
  total_tfiats: number;
  balance_deductivo: number;
  unrealized_profit: number;
  realized_profit: number;
  estrella_padre: number;
  abuelo: number;
  bisabuelo: number;
  host: number;
  host_del_host: number;
  company_pending: number;
  company_debt: number;
  company_deductive: number;
  stars: {
    guia: number;
    calificada: number;
    reincorporada: number;
  };
  total_stars: number;
  star_details?: Array<{
    id: number;
    type: string;
    created_at: string;
  }>;
}

interface DistributionSummary {
  distribution_data: TfiatDistributionData[];
  total_users: number;
  total_tfiats_system: number;
  total_stars_system: number;
}

const TfiatDistributionTable: React.FC = () => {
  const [data, setData] = useState<DistributionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Estados para modales
  const [showTfiatModal, setShowTfiatModal] = useState(false);
  const [showStarModal, setShowStarModal] = useState(false);
  const [showDeleteStarModal, setShowDeleteStarModal] = useState(false);
  const [showDeleteTfiatsModal, setShowDeleteTfiatsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<TfiatDistributionData | null>(null);
  const [selectedStar, setSelectedStar] = useState<{ id: number; type: string; user_email: string } | null>(null);
  
  // Estados para formularios
  const [tfiatAmount, setTfiatAmount] = useState('');
  const [tfiatReason, setTfiatReason] = useState('');
  const [starType, setStarType] = useState('guia');
  const [starReason, setStarReason] = useState('');
  const [deleteTfiatAmount, setDeleteTfiatAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDistributionData();
  }, []);

  const fetchDistributionData = async () => {
    try {
      setLoading(true);
      const response = await adminService.getTfiatDistributionTable();
      setData(response);
    } catch (error: any) {
      console.error('Error al obtener tabla de distribución:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al cargar tabla de distribución'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDistributionData();
    setRefreshing(false);
  };

  const handleAddTfiats = (user: TfiatDistributionData) => {
    setSelectedUser(user);
    setTfiatAmount('');
    setTfiatReason('');
    setShowTfiatModal(true);
  };

  const handleAddStars = (user: TfiatDistributionData) => {
    setSelectedUser(user);
    setStarType('guia');
    setStarReason('');
    setShowStarModal(true);
  };

  const submitTfiats = async () => {
    if (!selectedUser || !tfiatAmount || parseFloat(tfiatAmount) <= 0) {
      setMessage({ type: 'error', text: 'Por favor ingresa un monto válido' });
      return;
    }

    try {
      setSubmitting(true);
      await adminService.addManualTfiats({
        user_id: selectedUser.user_id,
        amount: parseFloat(tfiatAmount),
        reason: tfiatReason || 'Asignación manual por administrador'
      });
      
      setMessage({ type: 'success', text: `Se agregaron ${tfiatAmount} Tfiats a ${selectedUser.email}` });
      setShowTfiatModal(false);
      await fetchDistributionData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al agregar Tfiats'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitStars = async () => {
    if (!selectedUser || !starType) {
      setMessage({ type: 'error', text: 'Por favor selecciona un tipo de estrella' });
      return;
    }

    try {
      setSubmitting(true);
      await adminService.addManualStars({
        user_id: selectedUser.user_id,
        star_type: starType,
        reason: starReason || 'Asignación manual por administrador'
      });
      
      setMessage({ type: 'success', text: `Se agregó 1 estrella ${starType} a ${selectedUser.email}` });
      setShowStarModal(false);
      await fetchDistributionData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al agregar estrella'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStar = (starId: number, starType: string, userEmail: string) => {
    // Buscar el usuario correspondiente
    const user = data?.distribution_data.find(u => u.email === userEmail);
    if (user) {
      setSelectedUser(user);
      setSelectedStar({ id: starId, type: starType, user_email: userEmail });
      setShowDeleteStarModal(true);
    }
  };

  const handleDeleteTfiats = (user: TfiatDistributionData) => {
    setSelectedUser(user);
    setDeleteTfiatAmount('');
    setShowDeleteTfiatsModal(true);
  };

  const submitDeleteStar = async () => {
    if (!selectedStar || !selectedUser) return;

    try {
      setSubmitting(true);
      await adminService.deleteManualStarByType(selectedUser.user_id, selectedStar.type);
      
      setMessage({ type: 'success', text: `Estrella ${selectedStar.type} eliminada exitosamente de ${selectedStar.user_email}` });
      setShowDeleteStarModal(false);
      await fetchDistributionData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al eliminar estrella'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitDeleteTfiats = async () => {
    if (!selectedUser || !deleteTfiatAmount || parseFloat(deleteTfiatAmount) <= 0) {
      setMessage({ type: 'error', text: 'Por favor ingresa un monto válido' });
      return;
    }

    const amount = parseFloat(deleteTfiatAmount);
    if (amount > selectedUser.balance_deductivo) {
      setMessage({ type: 'error', text: `El usuario solo tiene ${selectedUser.balance_deductivo} Tfiats disponibles` });
      return;
    }

    try {
      setSubmitting(true);
      await adminService.deleteManualTfiats(selectedUser.user_id, amount);
      
      setMessage({ type: 'success', text: `Se eliminaron ${amount} Tfiats de ${selectedUser.email}` });
      setShowDeleteTfiatsModal(false);
      await fetchDistributionData();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Error al eliminar Tfiats'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (user: TfiatDistributionData) => {
    if (user.is_admin) return 'error';
    if (user.email === 'juan@example.com') return 'warning';
    return 'default';
  };

  const getStatusText = (user: TfiatDistributionData) => {
    if (user.is_admin) return 'Admin';
    if (user.email === 'juan@example.com') return 'Restringido';
    return 'Usuario';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🪙 Tabla de Distribución de Tfiats
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? <CircularProgress size={20} /> : 'Actualizar'}
        </Button>
      </Box>

      {/* Resumen del Sistema */}
      {data && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6">{data.total_users}</Typography>
                <Typography variant="body2">Total Usuarios</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
                <Typography variant="h6">{data.total_tfiats_system.toFixed(2)}</Typography>
                <Typography variant="body2">Total Tfiats Sistema</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
                <Typography variant="h6">{data.total_stars_system}</Typography>
                <Typography variant="body2">Total Estrellas Sistema</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                <Typography variant="h6">
                  {data.total_users > 0 ? (data.total_tfiats_system / data.total_users).toFixed(2) : '0.00'}
                </Typography>
                <Typography variant="body2">Promedio Tfiats/Usuario</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Mensajes */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Tabla de Distribución */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }} className="mobile-table-scroll">
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Total Tfiats</TableCell>
                <TableCell align="right">Balance Deductivo</TableCell>
                <TableCell align="right">Estrellas</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.distribution_data.map((user) => (
                <TableRow key={user.user_id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {user.full_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusText(user)} 
                      color={getStatusColor(user)}
                      size="small"
                      icon={user.is_admin ? <AdminIcon /> : <PersonIcon />}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {user.total_tfiats.toFixed(2)} TF
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {user.balance_deductivo.toFixed(2)} TF
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
                      {user.stars.guia > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={`${user.stars.guia}⭐`} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                          {!user.is_admin && user.email !== 'juan@example.com' && (
                            <Tooltip title="Eliminar estrella guía">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteStar(0, 'guia', user.email)}
                                sx={{ width: 20, height: 20 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {user.stars.calificada > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={`${user.stars.calificada}⭐`} 
                            size="small" 
                            color="success"
                            variant="outlined"
                          />
                          {!user.is_admin && user.email !== 'juan@example.com' && (
                            <Tooltip title="Eliminar estrella calificada">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteStar(0, 'calificada', user.email)}
                                sx={{ width: 20, height: 20 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {user.stars.reincorporada > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={`${user.stars.reincorporada}⭐`} 
                            size="small" 
                            color="warning"
                            variant="outlined"
                          />
                          {!user.is_admin && user.email !== 'juan@example.com' && (
                            <Tooltip title="Eliminar estrella reincorporada">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteStar(0, 'reincorporada', user.email)}
                                sx={{ width: 20, height: 20 }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      {user.total_stars === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Sin estrellas
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {!user.is_admin && user.email !== 'juan@example.com' && (
                        <>
                          <Tooltip title="Agregar Tfiats">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleAddTfiats(user)}
                            >
                              <MonetizationOnIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Agregar Estrella">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleAddStars(user)}
                            >
                              <StarIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar Tfiats">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteTfiats(user)}
                              disabled={user.balance_deductivo <= 0}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal para Agregar Tfiats */}
      <Dialog open={showTfiatModal} onClose={() => setShowTfiatModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Tfiats Manualmente</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Usuario: <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})
            </Typography>
            <TextField
              fullWidth
              label="Cantidad de Tfiats"
              type="number"
              value={tfiatAmount}
              onChange={(e) => setTfiatAmount(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ min: "0.01", step: "0.01" }}
            />
            <TextField
              fullWidth
              label="Razón (opcional)"
              value={tfiatReason}
              onChange={(e) => setTfiatReason(e.target.value)}
              placeholder="Asignación manual por administrador"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTfiatModal(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button 
            onClick={submitTfiats} 
            variant="contained" 
            disabled={submitting || !tfiatAmount || parseFloat(tfiatAmount) <= 0}
            startIcon={submitting ? <CircularProgress size={20} /> : <MonetizationOnIcon />}
          >
            {submitting ? 'Agregando...' : 'Agregar Tfiats'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para Agregar Estrellas */}
      <Dialog open={showStarModal} onClose={() => setShowStarModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Estrella Manualmente</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Usuario: <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Estrella</InputLabel>
              <Select
                value={starType}
                label="Tipo de Estrella"
                onChange={(e: SelectChangeEvent) => setStarType(e.target.value)}
              >
                <MenuItem value="guia">Estrella Guía</MenuItem>
                <MenuItem value="calificada">Estrella Calificada</MenuItem>
                <MenuItem value="reincorporada">Estrella Reincorporada</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Razón (opcional)"
              value={starReason}
              onChange={(e) => setStarReason(e.target.value)}
              placeholder="Asignación manual por administrador"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStarModal(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button 
            onClick={submitStars} 
            variant="contained" 
            color="warning"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <StarIcon />}
          >
            {submitting ? 'Agregando...' : 'Agregar Estrella'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para Eliminar Estrella */}
      <Dialog open={showDeleteStarModal} onClose={() => setShowDeleteStarModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Eliminar Estrella</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                ¿Estás seguro de que quieres eliminar esta estrella?
              </Typography>
              <Typography variant="body2">
                Esta acción no se puede deshacer y eliminará permanentemente la estrella del sistema.
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Estrella:</strong> {selectedStar?.type}<br />
              <strong>Usuario:</strong> {selectedStar?.user_email}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteStarModal(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button 
            onClick={submitDeleteStar} 
            variant="contained" 
            color="error"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {submitting ? 'Eliminando...' : 'SÍ, ELIMINAR'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para Eliminar Tfiats */}
      <Dialog open={showDeleteTfiatsModal} onClose={() => setShowDeleteTfiatsModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Eliminar Tfiats</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                ¿Estás seguro de que quieres eliminar Tfiats de este usuario?
              </Typography>
              <Typography variant="body2">
                Esta acción no se puede deshacer y reducirá permanentemente el balance del usuario.
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Usuario: <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})<br />
              <strong>Balance actual:</strong> {selectedUser?.balance_deductivo.toFixed(2)} TF
            </Typography>
            <TextField
              fullWidth
              label="Cantidad de Tfiats a eliminar"
              type="number"
              value={deleteTfiatAmount}
              onChange={(e) => setDeleteTfiatAmount(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ 
                min: "0.01", 
                step: "0.01",
                max: selectedUser?.balance_deductivo || 0
              }}
              helperText={`Máximo disponible: ${selectedUser?.balance_deductivo.toFixed(2)} TF`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteTfiatsModal(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button 
            onClick={submitDeleteTfiats} 
            variant="contained" 
            color="error"
            disabled={submitting || !deleteTfiatAmount || parseFloat(deleteTfiatAmount) <= 0 || parseFloat(deleteTfiatAmount) > (selectedUser?.balance_deductivo || 0)}
            startIcon={submitting ? <CircularProgress size={20} /> : <RemoveIcon />}
          >
            {submitting ? 'Eliminando...' : 'SÍ, ELIMINAR'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TfiatDistributionTable;
